import React, { useState } from "react";
import { Control, RegisterOptions, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ClickIcon from "../../icon-buttons/click-icon/ClickIcon";
import "./AcedInput.scss";

type BaseInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "name" | "value" | "onChange" | "placeholder"
> & { label?: string; placeholder?: string };

// --- Mode-specific props ---
type RHFProps = {
  control: Control<any, any>;
  name: string;
  rules?: RegisterOptions;
  value?: never;
  onChange?: never;
};

type ControlledProps = {
  value: string;
  onChange: (value: string) => void;
  control?: never;
  name?: never;
  rules?: never;
};

type UncontrolledProps = {
  defaultValue?: string;
  control?: never;
  name?: never;
  value?: never;
  onChange?: never;
  rules?: never;
};

// --- Combined type ---
type AcedInputProps = BaseInputProps &
  (RHFProps | ControlledProps | UncontrolledProps);

// --- Type guards ---
function isRHFProps(props: AcedInputProps): props is BaseInputProps & RHFProps {
  return "control" in props && "name" in props;
}

function isControlledProps(
  props: AcedInputProps
): props is BaseInputProps & ControlledProps {
  return "value" in props && "onChange" in props;
}

// --- Main Component ---
const AcedInput = React.forwardRef<HTMLInputElement, AcedInputProps>(
  (props, ref) => {
    const selectInput = () => {
      if (isRHFProps(props)) {
        return <AcedInputRHF {...props} />;
      } else if (isControlledProps(props)) {
        return <AcedInputControlled {...props} ref={ref} />;
      } else {
        return <AcedInputUncontrolled {...props} ref={ref} />;
      }
    };
    return <>{selectInput()}</>;
  }
);

AcedInput.displayName = "AcedInput";

export default AcedInput;

// --- RHF Version ---
const AcedInputRHF: React.FC<BaseInputProps & RHFProps> = ({
  control,
  name,
  rules,
  label,
  placeholder,
  ...rest
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setCapsLockOn] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const isPasswordType = rest.type === "password";

  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const { error } = fieldState;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.getModifierState && e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  // Check for autofilled or prefilled values
  const checkValue = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    setHasValue(!!inputValue);
  };

  return (
    <div className="input-grand-container">
      {label && (
        <div className="label-container">
          <label className="select-label">
            {label + (rules?.required ? "*" : "")}
          </label>
        </div>
      )}
      <div className="input-container position-relative">
        <input
          {...field}
          value={field.value ?? ""}
          {...rest}
          className="text-input"
          type={isPasswordType && showPassword ? "text" : rest.type}
          onKeyDown={isPasswordType ? handleKeyDown : rest.onKeyDown}
          onInput={checkValue}
          onAnimationStart={checkValue}
        />
        <label
          className={`placeholder-label ${
            (field.value !== undefined && field.value !== "" && field.value !== null) || hasValue || rest.type === "date" ? "filled" : ""
          }`}
        >
          {placeholder}
        </label>
        {isPasswordType && (
          <div className="toggle-password-btn">
            <ClickIcon
              color="black"
              icon={showPassword ? "eyeslash" : "eye"}
              size="s"
              onClick={() => setShowPassword((prev) => !prev)}
            />
          </div>
        )}
      </div>
      {error && <p className="error-label">{error.message}</p>}
      {isPasswordType && isCapsLockOn && (
        <p className="error-label">{t("common.warnings.capsLockOn")}</p>
      )}
    </div>
  );
};

// --- Controlled Version ---
const AcedInputControlled = React.forwardRef<
  HTMLInputElement,
  BaseInputProps & ControlledProps
>(({ value, onChange, label, placeholder, ...rest }, ref) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setCapsLockOn] = useState(false);
  const isPasswordType = rest.type === "password";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.getModifierState && e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  return (
    <div className="input-grand-container">
      {label && (
        <div className="label-container">
          <label className="select-label">{label}</label>
        </div>
      )}
      <div className="input-container position-relative">
        <input
          {...rest}
          ref={ref}
          className="text-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type={isPasswordType && showPassword ? "text" : rest.type}
          onKeyDown={isPasswordType ? handleKeyDown : rest.onKeyDown}
        />
        <label className={`placeholder-label ${value ? "filled" : ""}`}>
          {placeholder}
        </label>
        {isPasswordType && (
          <div className="toggle-password-btn">
            <ClickIcon
              color="black"
              icon={showPassword ? "eyeslash" : "eye"}
              size="s"
              onClick={() => setShowPassword((prev) => !prev)}
            />
          </div>
        )}
      </div>
      {isPasswordType && isCapsLockOn && (
        <p className="error-label">{t("common.warnings.capsLockOn")}</p>
      )}
    </div>
  );
});

AcedInputControlled.displayName = "AcedInputControlled";

// --- Uncontrolled Version ---
const AcedInputUncontrolled = React.forwardRef<
  HTMLInputElement,
  BaseInputProps & UncontrolledProps
>(({ defaultValue = "", label, placeholder, ...rest }, ref) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setCapsLockOn] = useState(false);
  const isPasswordType = rest.type === "password";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.getModifierState && e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  return (
    <div className="input-grand-container">
      {label && (
        <div className="label-container">
          <label className="select-label">{label}</label>
        </div>
      )}
      <div className="input-container position-relative">
        <input
          {...rest}
          ref={ref}
          className="text-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type={isPasswordType && showPassword ? "text" : rest.type}
          onKeyDown={isPasswordType ? handleKeyDown : rest.onKeyDown}
        />
        <label className={`placeholder-label ${value ? "filled" : ""}`}>
          {placeholder}
        </label>
        {isPasswordType && (
          <div className="toggle-password-btn">
            <ClickIcon
              color="black"
              icon={showPassword ? "eyeslash" : "eye"}
              size="s"
              onClick={() => setShowPassword((prev) => !prev)}
            />
          </div>
        )}
      </div>
      {isPasswordType && isCapsLockOn && (
        <p className="error-label">{t("common.warnings.capsLockOn")}</p>
      )}
    </div>
  );
});

AcedInputUncontrolled.displayName = "AcedInputUncontrolled";
